import axios from 'axios';

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;

export const http = axios.create({ baseURL: API });

// attach token if exists for admin endpoints
http.interceptors.request.use((cfg) => {
  const token = localStorage.getItem('yekuv_token');
  if (token && cfg.url?.includes('/admin/')) {
    cfg.headers.Authorization = `Bearer ${token}`;
  }
  return cfg;
});

http.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401 && err.config?.url?.includes('/admin/')) {
      localStorage.removeItem('yekuv_token');
      if (!window.location.pathname.includes('/admin/login')) {
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(err);
  }
);

// Public
export const api = {
  // news
  listNews: (category) => http.get('/news', { params: category && category !== 'Tümü' ? { category } : {} }).then(r => r.data),
  getNews: (slug) => http.get(`/news/${slug}`).then(r => r.data),
  // submissions
  createDonation: (data) => http.post('/donations', data).then(r => r.data),
  createScholarship: (data) => http.post('/scholarships', data).then(r => r.data),
  createVolunteer: (data) => http.post('/volunteers', data).then(r => r.data),
  uploadVolunteerPhoto: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return http.post('/volunteers/photo', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
  createMessage: (data) => http.post('/messages', data).then(r => r.data),
  subscribeNewsletter: (email) => http.post('/newsletter', { email }).then(r => r.data),
  publicStats: () => http.get('/stats/summary').then(r => r.data),
  // admin
  adminLogin: (username, password) => http.post('/admin/login', { username, password }).then(r => r.data),
  adminMe: () => http.get('/admin/me').then(r => r.data),
  adminDashboard: () => http.get('/admin/dashboard').then(r => r.data),
  adminDonations: () => http.get('/admin/donations').then(r => r.data),
  adminUpdateDonationStatus: (id, status) => http.patch(`/admin/donations/${id}/status`, { status }).then(r => r.data),
  adminDeleteDonation: (id) => http.delete(`/admin/donations/${id}`).then(r => r.data),
  adminScholarships: () => http.get('/admin/scholarships').then(r => r.data),
  adminVolunteers: () => http.get('/admin/volunteers').then(r => r.data),
  adminVolunteersTrash: () => http.get('/admin/volunteers/trash').then(r => r.data),
  adminVolunteerStatus: (id, status) => http.patch(`/admin/volunteers/${id}/status`, { status }).then(r => r.data),
  adminVolunteerDelete: (id) => http.delete(`/admin/volunteers/${id}`).then(r => r.data),
  adminVolunteerRestore: (id) => http.post(`/admin/volunteers/${id}/restore`).then(r => r.data),
  adminVolunteerPurge: (id) => http.delete(`/admin/volunteers/trash/${id}`).then(r => r.data),
  adminMessages: () => http.get('/admin/messages').then(r => r.data),
  adminMarkRead: (id) => http.patch(`/admin/messages/${id}/read`).then(r => r.data),
  adminUpdateScholarshipStatus: (id, status) => http.patch(`/admin/scholarships/${id}/status`, { status }).then(r => r.data),
  adminListNews: () => http.get('/admin/news').then(r => r.data),
  adminCreateNews: (data) => http.post('/admin/news', data).then(r => r.data),
  adminUpdateNews: (id, data) => http.patch(`/admin/news/${id}`, data).then(r => r.data),
  adminDeleteNews: (id) => http.delete(`/admin/news/${id}`).then(r => r.data),

  // -- v2: admin dashboard with analytics
  adminDashboardV2: () => http.get('/admin/dashboard/v2').then(r => r.data),

  // -- analytics
  trackPageview: (data) => http.post('/track/pageview', data).then(r => r.data).catch(() => null),
  adminAnalyticsSummary: (days = 30) => http.get('/admin/analytics/summary', { params: { days } }).then(r => r.data),
  adminAnalyticsLive: () => http.get('/admin/analytics/live').then(r => r.data),

  // -- settings
  publicSettings: () => http.get('/settings').then(r => r.data),
  adminListSettings: () => http.get('/admin/settings').then(r => r.data),
  adminUpsertSetting: (key, value) => http.put(`/admin/settings/${key}`, { value }).then(r => r.data),

  // -- hero slides
  publicHeroSlides: () => http.get('/hero-slides').then(r => r.data),
  adminListHeroSlides: () => http.get('/admin/hero-slides').then(r => r.data),
  adminCreateHero: (data) => http.post('/admin/hero-slides', data).then(r => r.data),
  adminUpdateHero: (id, data) => http.put(`/admin/hero-slides/${id}`, data).then(r => r.data),
  adminDeleteHero: (id) => http.delete(`/admin/hero-slides/${id}`).then(r => r.data),

  // -- generic content collections
  publicContent: (name) => http.get(`/content/${name}`).then(r => r.data),
  adminListContent: (name) => http.get(`/admin/content/${name}`).then(r => r.data),
  adminCreateContent: (name, data, sort_order = 0) => http.post(`/admin/content/${name}`, { data, sort_order, published: true }).then(r => r.data),
  adminUpdateContent: (name, id, data, sort_order = 0, published = true) => http.put(`/admin/content/${name}/${id}`, { data, sort_order, published }).then(r => r.data),
  adminDeleteContent: (name, id) => http.delete(`/admin/content/${name}/${id}`).then(r => r.data),

  // -- uploads
  adminUploadFile: (file, displayName, category) => {
    const fd = new FormData();
    fd.append('file', file);
    if (displayName && displayName.trim()) fd.append('display_name', displayName.trim());
    if (category && category.trim()) fd.append('category', category.trim());
    return http.post('/admin/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
  adminListUploads: () => http.get('/admin/uploads').then(r => r.data),
  adminRenameUpload: (name, display_name) => http.patch(`/admin/uploads/${encodeURIComponent(name)}`, { display_name }).then(r => r.data),
  adminUpdateUploadCategory: (name, category) => http.patch(`/admin/uploads/${encodeURIComponent(name)}`, { category }).then(r => r.data),
  adminDeleteUpload: (name) => http.delete(`/admin/uploads/${encodeURIComponent(name)}`).then(r => r.data),

  // -- logs
  adminListLogs: (limit = 100) => http.get('/admin/logs', { params: { limit } }).then(r => r.data),
  bumpSiteVersion: () => http.post('/admin/site-version/bump').then(r => r.data),

  // -- iyzico payment
  iyzicoInitiate: (data) => http.post('/payments/iyzico/initiate', data).then(r => r.data),
  iyzicoStatus: (id) => http.get(`/payments/iyzico/status/${id}`).then(r => r.data),

  // -- admin user management
  adminListUsers: () => http.get('/admin/users').then(r => r.data),
  adminCreateUser: (data) => http.post('/admin/users', data).then(r => r.data),
  adminUpdateUser: (id, data) => http.patch(`/admin/users/${id}`, data).then(r => r.data),
  adminDeleteUser: (id) => http.delete(`/admin/users/${id}`).then(r => r.data),
  adminChangePassword: (current_password, new_password) => http.post('/admin/change-password', { current_password, new_password }).then(r => r.data),

  // -- backup / restore
  adminBackupExport: (includeLogs = false) => http.get('/admin/backup/export', { params: { include_logs: includeLogs ? 1 : 0 } }).then(r => r.data),
  adminBackupSnapshot: () => http.post('/admin/backup/snapshot').then(r => r.data),
  adminBackupList: () => http.get('/admin/backup/list').then(r => r.data),
  adminBackupDelete: (name) => http.delete(`/admin/backup/${encodeURIComponent(name)}`).then(r => r.data),
  adminBackupTrashList: () => http.get('/admin/backup/trash').then(r => r.data),
  adminBackupTrashRestore: (name) => http.post(`/admin/backup/trash/${encodeURIComponent(name)}/restore`).then(r => r.data),
  adminBackupTrashPurge: (name) => http.delete(`/admin/backup/trash/${encodeURIComponent(name)}`).then(r => r.data),
  adminBackupTrashEmpty: () => http.delete('/admin/backup/trash').then(r => r.data),
  adminBackupRestore: (data) => http.post('/admin/backup/restore', data).then(r => r.data),
  adminBackupRestoreUpload: (file, opts = {}) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('mode', opts.mode || 'replace');
    fd.append('keep_admin_users', opts.keep_admin_users !== false ? 'true' : 'false');
    fd.append('restore_uploads', opts.restore_uploads !== false ? 'true' : 'false');
    return http.post('/admin/backup/restore-upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000, // 5 min for large zips
    }).then(r => r.data);
  },
  adminBackupDownloadUrl: (name) => `${API}/admin/backup/download/${encodeURIComponent(name)}`,

  // -- email templates
  adminEmailPreview: (data) => http.post('/admin/email/preview', data).then(r => r.data),
  adminEmailDefaults: (type) => http.get(`/admin/email/defaults/${type}`).then(r => r.data),
  adminEmailTest: (data) => http.post('/admin/email/test', data).then(r => r.data),

  // -- menu (nav) & custom pages
  publicMenu: () => http.get('/menu').then(r => r.data),
  publicPage: (slug) => http.get(`/pages/${slug}`).then(r => r.data),
  adminListMenu: () => http.get('/admin/menu').then(r => r.data),
  adminCreateMenu: (data) => http.post('/admin/menu', data).then(r => r.data),
  adminUpdateMenu: (id, data) => http.put(`/admin/menu/${id}`, data).then(r => r.data),
  adminDeleteMenu: (id) => http.delete(`/admin/menu/${id}`).then(r => r.data),
  adminReorderMenu: (items) => http.put('/admin/menu/reorder', { items }).then(r => r.data),
  adminListPages: () => http.get('/admin/pages').then(r => r.data),
  adminCreatePage: (data) => http.post('/admin/pages', data).then(r => r.data),
  adminUpdatePage: (id, data) => http.put(`/admin/pages/${id}`, data).then(r => r.data),
  adminDeletePage: (id) => http.delete(`/admin/pages/${id}`).then(r => r.data),
};
